import { Box, Divider, Flex, Form, Grid, Radio, Select, Text, TextField } from '@approvalmax/ui/src/components';
import { domain } from 'modules/data';
import { memo, ReactNode, useMemo } from 'react';

import { schedulerFormNames, yearsItems, yearsNumberItems } from '../../Scheduler.constants';
import { useSchedulerIncludeYearEdit, useSchedulerRadioChecked } from './SchedulerYear.hooks';
import { messages } from './SchedulerYear.messages';

export const SchedulerYear = memo(() => {
    const {
        schedulerIncludeStartYear,
        schedulerIncludeEndYear,
        handleIncludeStartYearChange,
        handleIncludeEndYearChange,
    } = useSchedulerIncludeYearEdit();
    const { schedulerRadioChecked, handleRadioCheckedChange } = useSchedulerRadioChecked();

    const specificYearsNumberItems = useMemo(() => {
        if (schedulerIncludeEndYear <= schedulerIncludeStartYear) {
            return [];
        }

        return Array.from({ length: schedulerIncludeEndYear - schedulerIncludeStartYear + 1 }, (_, i) => ({
            id: (schedulerIncludeStartYear + i).toString(),
            name: (schedulerIncludeStartYear + i).toString(),
        }));
    }, [schedulerIncludeEndYear, schedulerIncludeStartYear]);

    return (
        <Form.Part>
            <Radio.Group
                direction='column'
                spacing='16'
                value={schedulerRadioChecked}
                name={schedulerFormNames.yearOption}
                onChange={handleRadioCheckedChange}
            >
                <Radio value={domain.ScheduleYearType.EveryYear} size='small'>
                    {messages.everyYear}
                </Radio>

                <Divider />

                <Flex alignItems='center' spacing='4'>
                    <Radio value={domain.ScheduleYearType.EveryYearsStartingIn} size='small'>
                        {messages.every}

                        {schedulerRadioChecked !== domain.ScheduleYearType.EveryYearsStartingIn && (
                            <Text as='span' fontSize='small' font='label'>
                                {messages.everyYearsStartingIn({
                                    b: (chunks: ReactNode) => <b>{chunks}</b>,
                                })}
                            </Text>
                        )}
                    </Radio>

                    {schedulerRadioChecked === domain.ScheduleYearType.EveryYearsStartingIn && (
                        <Flex alignItems='center' spacing='8'>
                            <Box width={64}>
                                <Select name={schedulerFormNames.yearPeriod} size='small' items={yearsNumberItems} />
                            </Box>

                            <Text fontSize='small' font='label'>
                                {messages.startingYear}
                            </Text>

                            <Box width={66}>
                                <Select name={schedulerFormNames.startYear} size='small' items={yearsItems} />
                            </Box>
                        </Flex>
                    )}
                </Flex>

                <Divider />

                <Flex direction='column' spacing='4'>
                    <Radio value={domain.ScheduleYearType.SpecificYear} size='small'>
                        {messages.specificYear}
                    </Radio>

                    {schedulerRadioChecked === domain.ScheduleYearType.SpecificYear && (
                        <Flex direction='column' spacing='8'>
                            <Text fontSize='small' font='body' color='midnight70' spacing='4 0 0 24'>
                                {messages.includeYears}
                            </Text>

                            <Grid
                                gap='4px'
                                alignItems='center'
                                gridTemplateColumns='1fr auto 1fr'
                                width='170px'
                                padding='0 0 0 24'
                            >
                                <TextField
                                    name={schedulerFormNames.specificYearStart}
                                    onChange={(value) => handleIncludeStartYearChange(Number(value))}
                                    size='small'
                                    type='number'
                                />

                                <Text fontSize='small' font='label'>
                                    {messages.to}
                                </Text>

                                <TextField
                                    name={schedulerFormNames.specificYearEnd}
                                    onChange={(value) => handleIncludeEndYearChange(Number(value))}
                                    size='small'
                                    type='number'
                                />
                            </Grid>

                            <Text fontSize='small' font='body' color='midnight70' spacing='4 0 0 24'>
                                {messages.excludeYears}
                            </Text>

                            <Box width={240 + 24} spacing='0 0 0 24'>
                                <Select
                                    name={schedulerFormNames.specificYearExcluded}
                                    size='small'
                                    items={specificYearsNumberItems}
                                    placeholder={schedulerIncludeStartYear?.toString()}
                                    multiple
                                />
                            </Box>
                        </Flex>
                    )}
                </Flex>

                <Divider />

                <Flex alignItems='center' spacing='4'>
                    <Radio value={domain.ScheduleYearType.EveryYearBetween} size='small'>
                        {messages.yearBetween}

                        {schedulerRadioChecked !== domain.ScheduleYearType.EveryYearBetween && (
                            <Text as='span' fontSize='small' font='label'>
                                {messages.everyYearBetween({
                                    b: (chunks: ReactNode) => <b>{chunks}</b>,
                                })}
                            </Text>
                        )}
                    </Radio>

                    {schedulerRadioChecked === domain.ScheduleYearType.EveryYearBetween && (
                        <Flex alignItems='center' spacing='8'>
                            <Text fontSize='small' font='label'>
                                {messages.yearBetween}
                            </Text>

                            <Box width={66}>
                                <Select name={schedulerFormNames.yearBetweenStart} size='small' items={yearsItems} />
                            </Box>

                            <Text fontSize='small' font='label'>
                                {messages.and}
                            </Text>

                            <Box width={66}>
                                <Select name={schedulerFormNames.yearBetweenEnd} size='small' items={yearsItems} />
                            </Box>
                        </Flex>
                    )}
                </Flex>
            </Radio.Group>
        </Form.Part>
    );
});

SchedulerYear.displayName = 'SchedulerYear';
